import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import PharmeasyLayout from "@/components/PharmeasyLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Truck, RefreshCw, Phone, User, 
  Package, IndianRupee, Clock, CheckCircle2,
  ArrowLeft, MapPin, ExternalLink, Route, ArrowUpDown,
  Navigation, List, Map as MapIcon, Home, Copy, TrafficCone, ChevronDown, AlertCircle, Loader2,
  FileText, Download, Sparkles, Star, ShieldAlert
} from "lucide-react";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

const PharmeasyTripDetail = ({ user, onLogout }) => {
  const { tripId } = useParams();
  const navigate = useNavigate();
  
  const [trip, setTrip] = useState(null);
  const [tripShipments, setTripShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncingTasks, setSyncingTasks] = useState(false);
  const [optimizingRoute, setOptimizingRoute] = useState(false);
  const [sortByEdgSequence, setSortByEdgSequence] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [hubLocation, setHubLocation] = useState(null);
  const [useLiveTraffic, setUseLiveTraffic] = useState(false);
  const [returnToHub, setReturnToHub] = useState(false);
  const [showOptimizeOptions, setShowOptimizeOptions] = useState(false);
  const [cashBreakdown, setCashBreakdown] = useState(null);
  const [optimizationLogs, setOptimizationLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [showAiPanel, setShowAiPanel] = useState(false);

  useEffect(() => {
    if (tripId) {
      fetchTripDetail();
    }
  }, [tripId]);

  const fetchTripDetail = async () => {
    setLoading(true);
    try {
      // Fetch trip info (includes hubCoordinates, hubAddress, hubName)
      const tripRes = await axios.get(`${API}/pharmeasy-orders/trips/${tripId}`);
      const tripData = tripRes.data;
      setTrip(tripData);
      
      // Set hub location from trip data
      if (tripData.hubCoordinates) {
        setHubLocation({
          name: tripData.hubName || "Hub",
          coordinates: tripData.hubCoordinates,
          address: tripData.hubAddress || "",
        });
      } else {
        // Fallback: fetch from hub-location endpoint
        try {
          const hubRes = await axios.get(`${API}/pharmeasy-orders/trips/${tripId}/hub-location`);
          setHubLocation(hubRes.data);
        } catch {
          setHubLocation(null);
        }
      }
      
      // Fetch trip shipments with cash breakdown
      const shipmentsRes = await axios.get(`${API}/pharmeasy-orders/trips/${tripId}/shipments`);
      setTripShipments(shipmentsRes.data.data || []);
      setCashBreakdown(shipmentsRes.data.cash_breakdown || null);
    } catch (e) {
      toast.error("Failed to load trip details");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const syncTripTasks = async () => {
    setSyncingTasks(true);
    try {
      await axios.post(`${API}/pharmeasy-orders/trips/${tripId}/sync-tasks`);
      toast.success("Trip tasks synced successfully");
      fetchTripDetail();
    } catch (e) {
      toast.error("Failed to sync trip tasks");
    } finally {
      setSyncingTasks(false);
    }
  };

  const fetchOptimizationLogs = async () => {
    try {
      const res = await axios.get(`${API}/pharmeasy-orders/trips/${tripId}/optimization-logs`);
      setOptimizationLogs(res.data.data || []);
    } catch {
      setOptimizationLogs([]);
    }
  };

  const runAiAnalysis = async () => {
    setAiAnalyzing(true);
    setShowAiPanel(true);
    try {
      const res = await axios.post(`${API}/pharmeasy-orders/trips/${tripId}/ai-analyze`);
      setAiAnalysis(res.data);
      if (res.data.error) {
        toast.error(`AI Analysis: ${res.data.error}`);
      } else {
        toast.success("AI analysis complete");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI analysis failed");
      setAiAnalysis({ error: e.response?.data?.detail || "Failed" });
    } finally {
      setAiAnalyzing(false);
    }
  };


  const optimizeRoute = async () => {
    setOptimizingRoute(true);
    setAiAnalysis(null);
    try {
      const res = await axios.post(`${API}/pharmeasy-orders/trips/${tripId}/optimize-route`, {
        use_live_traffic: useLiveTraffic,
        return_to_hub: returnToHub
      });
      toast.success(`Route optimized! ${res.data.optimized_count || 0} shipments sequenced`);
      fetchTripDetail();
      fetchOptimizationLogs();
      
      // AI analysis runs in background on backend — auto-fetch after delay
      setShowAiPanel(true);
      setAiAnalyzing(true);
      setTimeout(() => runAiAnalysis(), 10000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to optimize route");
      setShowAiPanel(false);
    } finally {
      setOptimizingRoute(false);
      setShowOptimizeOptions(false);
    }
  };

  // Get relevant address based on shipment type
  const getRelevantAddress = (shipment) => {
    if (shipment?.type === "RETURN") {
      return shipment?.srcAddress;
    }
    return shipment?.destAddress;
  };

  const getMapLink = (shipment) => {
    if (shipment?.type === "RETURN") {
      // RETURN: ONLY use srcAddress (customer pickup location)
      const calcSrc = shipment?.calculatedSrcAddressCoordinate;
      if (calcSrc?.success && calcSrc?.coordinates) {
        return `https://www.google.com/maps?q=${calcSrc.coordinates.lat},${calcSrc.coordinates.lng}`;
      }
      const srcAddr = shipment?.srcAddress;
      if (srcAddr?.latitude && srcAddr?.longitude) {
        const lat = parseFloat(srcAddr.latitude);
        const lng = parseFloat(srcAddr.longitude);
        if (lat !== 0 && lng !== 0 && !isNaN(lat) && !isNaN(lng)) {
          return `https://www.google.com/maps?q=${lat},${lng}`;
        }
      }
    } else {
      const calcCoords = shipment?.calculatedDestAddressCoordinate;
      if (calcCoords?.success && calcCoords?.coordinates) {
        return `https://www.google.com/maps?q=${calcCoords.coordinates.lat},${calcCoords.coordinates.lng}`;
      }
      const destAddr = shipment?.destAddress;
      if (destAddr?.latitude && destAddr?.longitude) {
        const lat = parseFloat(destAddr.latitude);
        const lng = parseFloat(destAddr.longitude);
        if (lat !== 0 && lng !== 0 && !isNaN(lat) && !isNaN(lng)) {
          return `https://www.google.com/maps?q=${lat},${lng}`;
        }
      }
    }
    return null;
  };

  const formatAddress = (addr) => {
    if (!addr) return "—";
    const parts = [addr.address1, addr.city, addr.pincode].filter(Boolean);
    return parts.join(", ") || "—";
  };

  const getCoordinates = (shipment) => {
    if (shipment?.type === "RETURN") {
      // RETURN: ONLY use srcAddress (customer pickup location)
      const calcSrc = shipment?.calculatedSrcAddressCoordinate;
      if (calcSrc?.success && calcSrc?.coordinates) {
        return calcSrc.coordinates;
      }
      const srcAddr = shipment?.srcAddress;
      if (srcAddr?.latitude && srcAddr?.longitude) {
        const lat = parseFloat(srcAddr.latitude);
        const lng = parseFloat(srcAddr.longitude);
        if (lat !== 0 && lng !== 0 && !isNaN(lat) && !isNaN(lng)) {
          return { lat, lng };
        }
      }
    } else {
      const calcCoords = shipment?.calculatedDestAddressCoordinate;
      if (calcCoords?.success && calcCoords?.coordinates) {
        return calcCoords.coordinates;
      }
      const destAddr = shipment?.destAddress;
      if (destAddr?.latitude && destAddr?.longitude) {
        const lat = parseFloat(destAddr.latitude);
        const lng = parseFloat(destAddr.longitude);
        if (lat !== 0 && lng !== 0 && !isNaN(lat) && !isNaN(lng)) {
          return { lat, lng };
        }
      }
    }
    return null;
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleString("en-IN", {
        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit"
      });
    } catch { return "—"; }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return `₹${amount.toLocaleString("en-IN")}`;
  };

  const getStatusColor = (status) => {
    const colors = {
      STARTED: "bg-blue-100 text-blue-800",
      COMPLETED: "bg-green-100 text-green-800",
      RECON_FINISHED: "bg-emerald-100 text-emerald-800",
      NOT_STARTED: "bg-slate-100 text-slate-800",
    };
    return colors[status] || "bg-slate-100 text-slate-800";
  };

  const getShipmentStatusColor = (status) => {
    const colors = {
      DELIVERED: "bg-green-100 text-green-800",
      CANCELLED: "bg-red-100 text-red-800",
      OUT_FOR_DELIVERY: "bg-blue-100 text-blue-800",
      UNASSIGNED: "bg-slate-100 text-slate-800",
      INWARDED: "bg-purple-100 text-purple-800",
      ASSIGNED: "bg-amber-100 text-amber-800",
    };
    return colors[status?.toUpperCase()] || "bg-slate-100 text-slate-800";
  };

  const hasEdgSequence = tripShipments.some(s => s.sequenceByEdg != null);

  const getSortedShipments = () => {
    if (!tripShipments.length) return [];
    
    return [...tripShipments].sort((a, b) => {
      if (sortByEdgSequence) {
        // Sort by sequenceByEdg if available, otherwise put at end
        const seqA = a.sequenceByEdg ?? 999999;
        const seqB = b.sequenceByEdg ?? 999999;
        return seqA - seqB;
      }
      // Default: sort by priority
      const pA = a.priority ?? 999;
      const pB = b.priority ?? 999;
      return pA - pB;
    });
  };

  const getStaticMapUrl = useCallback(() => {
    const shipmentsWithCoords = tripShipments
      .map((s, idx) => ({ ...s, idx: idx + 1, coords: getCoordinates(s) }))
      .filter(s => s.coords);

    if (shipmentsWithCoords.length === 0) return null;

    const hubCoord = hubLocation?.coordinates 
      ? `${hubLocation.coordinates.lat},${hubLocation.coordinates.lng}`
      : null;

    let markers = "";
    
    if (hubCoord) {
      markers += `&markers=color:green|label:H|${hubCoord}`;
    }

    shipmentsWithCoords.forEach((s) => {
      const label = s.sequenceByEdg || s.idx;
      markers += `&markers=color:red|label:${label}|${s.coords.lat},${s.coords.lng}`;
    });

    const center = shipmentsWithCoords[0].coords;
    
    return `https://maps.googleapis.com/maps/api/staticmap?center=${center.lat},${center.lng}&zoom=12&size=800x400&maptype=roadmap${markers}&key=${GOOGLE_MAPS_API_KEY}`;
  }, [tripShipments, hubLocation]);

  // Build Google Maps Embed URL with directions
  const buildDirectionsEmbedUrl = useCallback(() => {
    const sortedShipments = sortByEdgSequence 
      ? [...tripShipments].sort((a, b) => (a.sequenceByEdg ?? 999) - (b.sequenceByEdg ?? 999))
      : tripShipments;
    
    const waypoints = sortedShipments
      .map(s => getCoordinates(s))
      .filter(Boolean)
      .map(c => `${c.lat},${c.lng}`);
    
    if (waypoints.length === 0) return null;
    
    // Hub as origin
    const originCoords = hubLocation?.coordinates;
    const originStr = originCoords ? `${originCoords.lat},${originCoords.lng}` : waypoints[0];
    
    // If return to hub, destination is hub; all shipment coords are waypoints
    const destination = returnToHub && originCoords ? originStr : waypoints[waypoints.length - 1];
    const middlePoints = returnToHub && originCoords ? waypoints : waypoints.slice(0, -1);
    const waypointsStr = middlePoints.join('|');
    
    let url = `https://www.google.com/maps/embed/v1/directions?key=${GOOGLE_MAPS_API_KEY}&origin=${originStr}&destination=${destination}`;
    if (waypointsStr) {
      url += `&waypoints=${waypointsStr}`;
    }
    url += `&mode=driving`;
    
    return url;
  }, [tripShipments, hubLocation, sortByEdgSequence, returnToHub]);

  // Build full Google Maps directions URL
  const buildFullMapUrl = useCallback(() => {
    const sortedShipments = sortByEdgSequence 
      ? [...tripShipments].sort((a, b) => (a.sequenceByEdg ?? 999) - (b.sequenceByEdg ?? 999))
      : tripShipments;
    
    const waypoints = sortedShipments
      .map(s => getCoordinates(s))
      .filter(Boolean)
      .map(c => `${c.lat},${c.lng}`);
    
    if (waypoints.length === 0) return null;
    
    // Hub as origin
    const originCoords = hubLocation?.coordinates;
    const originStr = originCoords ? `${originCoords.lat},${originCoords.lng}` : waypoints[0];
    
    // If return to hub, append hub as final destination
    const destination = returnToHub && originCoords ? originStr : waypoints[waypoints.length - 1];
    const middlePoints = returnToHub && originCoords ? waypoints : waypoints.slice(0, -1);
    const middle = middlePoints.join('/');
    
    return middle 
      ? `https://www.google.com/maps/dir/${originStr}/${middle}/${destination}`
      : `https://www.google.com/maps/dir/${originStr}/${destination}`;
  }, [tripShipments, hubLocation, sortByEdgSequence, returnToHub]);

  if (loading) {
    return (
      <PharmeasyLayout user={user} onLogout={onLogout} title="Trip Detail">
        <div className="flex flex-col items-center justify-center h-[60vh]">
          <Loader2 className="w-12 h-12 animate-spin text-emerald-500" />
          <p className="text-slate-500 mt-4 text-lg">Loading Trip Details...</p>
        </div>
      </PharmeasyLayout>
    );
  }

  if (!trip) {
    return (
      <PharmeasyLayout user={user} onLogout={onLogout} title="Trip Detail">
        <div className="text-center py-12">
          <p className="text-slate-500">Trip not found</p>
          <Button onClick={() => navigate("/pharmeasy/trips")} className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Trips
          </Button>
        </div>
      </PharmeasyLayout>
    );
  }

  return (
    <PharmeasyLayout user={user} onLogout={onLogout} title={`Trip: ${trip.tripId}`}>
      <div className="space-y-6">
        {/* Back Button & Header */}
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => navigate("/pharmeasy/trips")}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Trips
          </Button>
          <div className="flex items-center gap-3">
            {!["COMPLETED", "RECON_FINISHED"].includes(trip.status) && (
              <Button
                variant="outline"
                size="sm"
                onClick={syncTripTasks}
                disabled={syncingTasks}
                className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${syncingTasks ? "animate-spin" : ""}`} />
                Sync
              </Button>
            )}
          </div>
        </div>

        {/* Trip Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <Truck className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Trip ID</p>
                  <p className="font-semibold text-slate-800">{trip.tripId}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-100">
                  <User className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Rider</p>
                  <p className="font-semibold text-slate-800">{trip.riderName || trip.agentName || "—"}</p>
                  {(trip.riderMobile || trip.phoneNumber) && (
                    <a href={`tel:${trip.riderMobile || trip.phoneNumber}`} className="text-xs text-emerald-600 flex items-center gap-1">
                      <Phone className="w-3 h-3" /> {trip.riderMobile || trip.phoneNumber}
                    </a>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cash Collection Breakdown */}
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-4">
              <p className="text-xs text-green-600 mb-2">Cash Collected</p>
              <p className="font-bold text-lg text-green-700">
                {formatCurrency(cashBreakdown?.cash_in_cash || 0)}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-violet-50 border-violet-200">
            <CardContent className="pt-4">
              <p className="text-xs text-violet-600 mb-2">QR Collected</p>
              <p className="font-bold text-lg text-violet-700">
                {formatCurrency(cashBreakdown?.cash_in_qr || 0)}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="pt-4">
              <p className="text-xs text-emerald-600 mb-2">Total Collected / To Collect</p>
              <p className="font-bold text-lg text-emerald-700">
                {formatCurrency(cashBreakdown?.total_collected || trip.cashCollected || 0)}
                <span className="text-sm font-normal text-slate-400"> / </span>
                <span className="text-amber-600">{formatCurrency(cashBreakdown?.total_collectable || trip.cashToBeCollected || 0)}</span>
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Status & Tasks Row */}
        <div className="flex flex-wrap items-center gap-4">
          <Badge className={`${getStatusColor(trip.status)} text-sm px-4 py-2`}>
            {trip.status?.replace("_", " ")}
          </Badge>
          <div className="flex items-center gap-2 text-slate-600">
            <Package className="w-4 h-4" />
            <span className="font-medium">{trip.taskCount || 0} Tasks</span>
          </div>
          {trip.tasks_count_synced != null && (
            <div className="flex items-center gap-2 text-emerald-600">
              <Package className="w-4 h-4" />
              <span className="font-medium">{trip.tasks_count_synced} Updated Tasks</span>
            </div>
          )}
        </div>

        {/* Hub Info */}
        {hubLocation && (
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-2">
                <Home className="w-4 h-4 text-emerald-600" />
                <span className="text-sm text-slate-600">Hub:</span>
                <span className="font-semibold text-emerald-700">{hubLocation.name || "Hub"}</span>
                {hubLocation.address && (
                  <span className="text-xs text-slate-500 ml-2 truncate max-w-md">{hubLocation.address}</span>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap items-center justify-between gap-4 bg-white p-4 rounded-lg border">
          <div className="flex items-center gap-4">
            <h3 className="font-semibold text-slate-800">Shipments ({tripShipments.length})</h3>
            {/* View Toggle */}
            <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5">
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className={`text-xs px-3 h-8 ${viewMode === 'list' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
              >
                <List className="w-4 h-4 mr-1" /> List
              </Button>
              <Button
                variant={viewMode === 'map' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('map')}
                className={`text-xs px-3 h-8 ${viewMode === 'map' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
              >
                <MapIcon className="w-4 h-4 mr-1" /> Map
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {hasEdgSequence && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSortByEdgSequence(!sortByEdgSequence)}
                className={sortByEdgSequence ? "bg-emerald-50 border-emerald-300 text-emerald-700" : ""}
              >
                <ArrowUpDown className="w-4 h-4 mr-1" />
                {sortByEdgSequence ? "Edg Seq" : "Priority"}
              </Button>
            )}
            {/* Optimize Button */}
            <div className="relative">
              <div className="flex">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={optimizeRoute}
                  disabled={optimizingRoute || tripShipments.length === 0}
                  className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-r-none border-r-0"
                >
                  <Route className={`w-4 h-4 mr-1 ${optimizingRoute ? "animate-pulse" : ""}`} />
                  {optimizingRoute ? "Optimizing..." : "Optimize Route"}
                  {useLiveTraffic && <TrafficCone className="w-3 h-3 ml-1 text-amber-500" />}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowOptimizeOptions(!showOptimizeOptions)}
                  disabled={optimizingRoute}
                  className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-l-none px-2"
                >
                  <ChevronDown className={`w-4 h-4 transition-transform ${showOptimizeOptions ? 'rotate-180' : ''}`} />
                </Button>
              </div>
              {showOptimizeOptions && (
                <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg z-50 min-w-[260px]">
                  <div className="p-4 space-y-3">
                    {hubLocation && (
                      <div className="pb-3 border-b">
                        <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                          <Home className="w-4 h-4 text-emerald-600" />
                          Hub: {hubLocation.name || "Hub"}
                        </div>
                        <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                          {hubLocation.address || `${hubLocation.coordinates?.lat?.toFixed(4)}, ${hubLocation.coordinates?.lng?.toFixed(4)}`}
                        </p>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                        <TrafficCone className="w-4 h-4 text-amber-500" />
                        Live Traffic
                      </label>
                      <button
                        onClick={() => setUseLiveTraffic(!useLiveTraffic)}
                        className={`w-10 h-5 rounded-full transition-colors ${useLiveTraffic ? 'bg-emerald-500' : 'bg-slate-300'}`}
                      >
                        <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${useLiveTraffic ? 'translate-x-5' : 'translate-x-0.5'}`} />
                      </button>
                    </div>
                    <p className="text-xs text-slate-500">
                      {useLiveTraffic ? 'Route considers current traffic' : 'Route based on distance only'}
                    </p>
                    <div className="flex items-center justify-between pt-2 border-t">
                      <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                        <Navigation className="w-4 h-4 text-blue-500" />
                        Return to Hub
                      </label>
                      <button
                        onClick={() => setReturnToHub(!returnToHub)}
                        className={`w-10 h-5 rounded-full transition-colors ${returnToHub ? 'bg-blue-500' : 'bg-slate-300'}`}
                      >
                        <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${returnToHub ? 'translate-x-5' : 'translate-x-0.5'}`} />
                      </button>
                    </div>
                    <p className="text-xs text-slate-500">
                      {returnToHub ? 'Route ends back at hub (round-trip)' : 'Route ends at last delivery (open)'}
                    </p>
                  </div>
                </div>
              )}
            </div>
            {/* Optimization Logs Button */}
            <Button
              variant="outline"
              size="sm"
              data-testid="view-optimization-logs-btn"
              onClick={() => { setShowLogs(!showLogs); if (!showLogs) fetchOptimizationLogs(); }}
              className="text-slate-600 hover:text-slate-700 hover:bg-slate-50"
            >
              <FileText className="w-4 h-4 mr-1" />
              Logs
            </Button>
            {/* AI Analysis Button */}
            <Button
              variant="outline"
              size="sm"
              data-testid="ai-analyze-trip-btn"
              onClick={runAiAnalysis}
              disabled={aiAnalyzing || tripShipments.length === 0}
              className="text-violet-600 hover:text-violet-700 hover:bg-violet-50 border-violet-200"
            >
              <Sparkles className={`w-4 h-4 mr-1 ${aiAnalyzing ? "animate-spin" : ""}`} />
              {aiAnalyzing ? "Analyzing..." : "AI Insights"}
            </Button>
          </div>
        </div>

        {/* Optimization Logs Panel */}
        {showLogs && (
          <Card data-testid="optimization-logs-panel">
            <CardHeader className="py-4 px-4">
              <CardTitle className="text-sm flex items-center gap-2">
                <FileText className="w-4 h-4 text-slate-600" />
                Optimization Logs ({optimizationLogs.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 pt-0">
              {optimizationLogs.length === 0 ? (
                <p className="text-sm text-slate-500">No optimization logs yet. Run "Optimize Route" to generate a log.</p>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {optimizationLogs.map((log, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border text-sm" data-testid={`optimization-log-${idx}`}>
                      <div className="flex items-center gap-4">
                        <div>
                          <span className="font-medium text-slate-800">{new Date(log.optimized_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                          <span className="text-slate-400 mx-2">by</span>
                          <span className="text-slate-700">{log.optimized_by}</span>
                        </div>
                        <Badge className="bg-slate-100 text-slate-700 text-xs">{log.strategy || "N/A"}</Badge>
                        <span className="text-xs text-slate-500">{log.total_shipments} shipments, {log.optimized_count} optimized</span>
                        {log.optimization_time_sec && (
                          <span className="text-xs text-slate-400">{log.optimization_time_sec}s</span>
                        )}
                        {log.settings?.live_traffic && <TrafficCone className="w-3 h-3 text-amber-500" />}
                        {log.settings?.return_to_hub && <Navigation className="w-3 h-3 text-blue-500" />}
                      </div>
                      {(log.download_url || log.log_url) && (
                        <a
                          href={log.download_url || log.log_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-emerald-600 hover:text-emerald-700 text-xs font-medium"
                          data-testid={`log-download-${idx}`}
                        >
                          <Download className="w-3.5 h-3.5" /> View Log
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* AI Analysis Panel */}
        {showAiPanel && (
          <Card data-testid="ai-analysis-panel" className="border-violet-200 bg-gradient-to-br from-violet-50/50 to-white">
            <CardHeader className="py-4 px-4">
              <CardTitle className="text-sm flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-violet-600" />
                  AI Route Analysis
                </div>
                <button onClick={() => setShowAiPanel(false)} className="text-slate-400 hover:text-slate-600 text-xs">Close</button>
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 pt-0">
              {aiAnalyzing ? (
                <div className="flex items-center gap-3 py-8 justify-center text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin text-violet-500" />
                  <span>Analyzing {tripShipments.length} shipments with AI...</span>
                </div>
              ) : aiAnalysis?.error ? (
                <div className="p-3 bg-red-50 rounded-lg text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  {aiAnalysis.error}
                </div>
              ) : aiAnalysis ? (
                <div className="space-y-4">
                  {/* Score + Summary */}
                  <div className="flex items-start gap-4">
                    <div className="text-center px-4 py-2 rounded-lg bg-white border shadow-sm">
                      <div className={`text-2xl font-bold ${aiAnalysis.route_quality_score >= 7 ? 'text-emerald-600' : aiAnalysis.route_quality_score >= 4 ? 'text-amber-600' : 'text-red-600'}`}>
                        {aiAnalysis.route_quality_score ? `${aiAnalysis.route_quality_score}/10` : '—'}
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5">Route Score</div>
                    </div>
                    {aiAnalysis.pre_validation && !aiAnalysis.pre_validation.skipped && (
                      <div className="text-center px-4 py-2 rounded-lg bg-white border shadow-sm">
                        <div className={`text-2xl font-bold ${aiAnalysis.pre_validation.confidence_pct >= 70 ? 'text-emerald-600' : aiAnalysis.pre_validation.confidence_pct >= 40 ? 'text-amber-600' : 'text-red-600'}`}>
                          {aiAnalysis.pre_validation.confidence_pct || '?'}%
                        </div>
                        <div className="text-[10px] text-slate-500 mt-0.5">Addr Quality</div>
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="text-sm text-slate-700 leading-relaxed">{aiAnalysis.trip_summary}</p>
                      {aiAnalysis.estimated_completion_hours && (
                        <div className="flex items-center gap-1 mt-2 text-xs text-slate-500">
                          <Clock className="w-3 h-3" /> Est. {aiAnalysis.estimated_completion_hours}h completion
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Risk Flags */}
                  {aiAnalysis.risk_flags?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-red-700 mb-1.5 flex items-center gap-1">
                        <ShieldAlert className="w-3.5 h-3.5" /> Risks
                      </h4>
                      <div className="space-y-1">
                        {aiAnalysis.risk_flags.map((flag, i) => (
                          <div key={i} className="text-sm text-red-700 bg-red-50 rounded px-2.5 py-1.5">{flag}</div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Address Issues */}
                  {aiAnalysis.address_issues?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-amber-700 mb-1.5 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5" /> Address Issues ({aiAnalysis.address_issues.length})
                      </h4>
                      <div className="space-y-1">
                        {aiAnalysis.address_issues.map((issue, i) => (
                          <div key={i} className="text-sm bg-amber-50 rounded px-2.5 py-1.5">
                            <span className="font-mono text-amber-800">{issue.shipment_id}</span>: {issue.issue}
                            {issue.suggestion && <span className="text-amber-600 ml-1">→ {issue.suggestion}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Route Insights */}
                  {aiAnalysis.route_insights?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-blue-700 mb-1.5 flex items-center gap-1">
                        <Route className="w-3.5 h-3.5" /> Route Insights
                      </h4>
                      <div className="space-y-1">
                        {aiAnalysis.route_insights.map((insight, i) => (
                          <div key={i} className="text-sm text-blue-700 bg-blue-50 rounded px-2.5 py-1.5">{insight}</div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Optimization Suggestions */}
                  {aiAnalysis.optimization_suggestions?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-emerald-700 mb-1.5 flex items-center gap-1">
                        <Star className="w-3.5 h-3.5" /> Suggestions
                      </h4>
                      <div className="space-y-1">
                        {aiAnalysis.optimization_suggestions.map((sug, i) => (
                          <div key={i} className="text-sm text-emerald-700 bg-emerald-50 rounded px-2.5 py-1.5">{sug}</div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </CardContent>
          </Card>
        )}

        {/* Map View */}
        {viewMode === 'map' && (
          <Card>
            <CardContent className="p-0 overflow-hidden rounded-lg">
              {(() => {
                const embedUrl = buildDirectionsEmbedUrl();
                const fullMapUrl = buildFullMapUrl();
                const sortedShipments = sortByEdgSequence 
                  ? [...tripShipments].sort((a, b) => (a.sequenceByEdg ?? 999) - (b.sequenceByEdg ?? 999))
                  : tripShipments;
                
                if (embedUrl) {
                  return (
                    <div className="relative">
                      {/* Interactive Google Maps iframe */}
                      <iframe
                        src={embedUrl}
                        width="100%"
                        height="500"
                        style={{ border: 0 }}
                        allowFullScreen=""
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title={`Trip ${trip.tripId} Route Map`}
                        className="w-full"
                      />
                      
                      {/* Hub/Pickup Info */}
                      <div className="absolute top-4 right-4 bg-emerald-600 text-white px-3 py-2 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2">
                        <Home className="w-4 h-4" />
                        <span>Pickup: {hubLocation?.name || "Hub"}</span>
                      </div>
                      
                      {/* Delivery Sequence panel */}
                      <div className="absolute top-4 left-4 bg-white/95 backdrop-blur rounded-lg p-3 max-h-80 overflow-y-auto shadow-lg">
                        <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-2">
                          <Truck className="w-4 h-4" /> 
                          Delivery Sequence {sortByEdgSequence ? "(Edg Optimized)" : "(Priority)"}:
                        </p>
                        {sortedShipments.filter(s => getCoordinates(s)).slice(0, 20).map((s, i) => (
                          <div key={s._id} className="text-xs text-slate-600 flex items-center gap-2 py-1">
                            <span className={`w-5 h-5 rounded-full ${s.type === "RETURN" ? "bg-amber-500" : "bg-blue-500"} text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0`}>
                              {sortByEdgSequence ? (s.sequenceByEdg || i+1) : (s.priority || i+1)}
                            </span>
                            <span className="truncate max-w-[200px]">
                              {s.shipmentId} - {getRelevantAddress(s)?.pincode || s.dropPincode}
                              {s.type === "RETURN" ? " (R)" : ""}
                            </span>
                          </div>
                        ))}
                        {sortedShipments.filter(s => getCoordinates(s)).length > 20 && (
                          <p className="text-xs text-slate-400 mt-1">+{sortedShipments.filter(s => getCoordinates(s)).length - 20} more stops</p>
                        )}
                      </div>
                      
                      {/* Open in Google Maps button */}
                      {fullMapUrl && (
                        <a 
                          href={fullMapUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="absolute bottom-4 right-4 bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700 flex items-center gap-2 shadow-lg"
                        >
                          <Navigation className="w-4 h-4" /> Open Full Route
                        </a>
                      )}
                    </div>
                  );
                } else {
                  return (
                    <div className="h-[500px] flex items-center justify-center bg-slate-100">
                      <p className="text-slate-500">No coordinates available for map</p>
                    </div>
                  );
                }
              })()}
            </CardContent>
          </Card>
        )}

        {/* Shipments Table */}
        {viewMode === 'list' && (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Priority</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Edg Seq</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Type</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Shipment ID</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Reference ID</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">LBN</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Address</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Contact</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Pincode</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Payment</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">COD Amt</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Actual Pay</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Map</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getSortedShipments().map((shipment, idx) => {
                      const mapLink = getMapLink(shipment);
                      const relevantAddr = getRelevantAddress(shipment);
                      const fullAddress = formatAddress(relevantAddr);
                      const contactNumber = relevantAddr?.contactNumber || shipment?.recipientMobileNo;
                      return (
                        <tr key={shipment._id} className="border-t hover:bg-slate-50">
                          <td className="px-4 py-3 align-middle">
                            <span className="text-slate-600 text-sm font-medium">{shipment.priority || "—"}</span>
                          </td>
                          <td className="px-4 py-3 align-middle">
                            {shipment.sequenceByEdg != null ? (
                              <Badge className="bg-emerald-100 text-emerald-800 text-xs font-semibold">
                                {shipment.sequenceByEdg}
                              </Badge>
                            ) : (
                              <span className="text-slate-400 text-sm">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 align-middle">
                            <Badge className={`${getShipmentStatusColor(shipment.bolt_status || shipment.status)} text-xs`}>
                              {(shipment.bolt_status || shipment.status)?.replace(/_/g, " ") || "—"}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 align-middle">
                            <Badge className={`text-xs ${shipment.type === "RETURN" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>
                              {shipment.type || "—"}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 align-middle">
                            <span className="font-mono text-sm font-medium">{shipment.shipmentId}</span>
                          </td>
                          <td className="px-4 py-3 align-middle">
                            <div className="flex items-center gap-1">
                              <span className="text-sm font-mono text-slate-600 truncate max-w-[100px]" title={shipment.referenceId}>
                                {shipment.referenceId || "—"}
                              </span>
                              {shipment.referenceId && (
                                <button onClick={() => copyToClipboard(shipment.referenceId)} className="text-slate-400 hover:text-emerald-600">
                                  <Copy className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 align-middle">
                            <span className="text-sm font-mono">{shipment.lbn || "—"}</span>
                          </td>
                          <td className="px-4 py-3 align-middle max-w-[200px]">
                            <div className="flex items-center gap-1">
                              <span className="text-sm text-slate-600 truncate" title={fullAddress}>
                                {fullAddress}
                              </span>
                              {fullAddress !== "—" && (
                                <button onClick={() => copyToClipboard(fullAddress)} className="text-slate-400 hover:text-emerald-600 flex-shrink-0">
                                  <Copy className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 align-middle">
                            {contactNumber ? (
                              <a href={`tel:${contactNumber}`} className="text-sm text-emerald-600 flex items-center gap-1">
                                <Phone className="w-3 h-3" /> {contactNumber}
                              </a>
                            ) : (
                              <span className="text-sm text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 align-middle">
                            <span className="text-sm text-slate-600">{relevantAddr?.pincode || shipment.dropPincode || "—"}</span>
                          </td>
                          <td className="px-4 py-3 align-middle">
                            <Badge className={`text-xs ${shipment.paymentType === "COD" ? "bg-orange-100 text-orange-800" : "bg-sky-100 text-sky-800"}`}>
                              {shipment.paymentType || "—"}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 align-middle text-right">
                            <span className={`text-sm font-medium ${shipment.collectableAmount > 0 ? "text-amber-700" : "text-slate-400"}`}>
                              {shipment.collectableAmount ? `₹${shipment.collectableAmount}` : "—"}
                            </span>
                          </td>
                          <td className="px-4 py-3 align-middle">
                            {shipment.actualPaymentType ? (
                              <Badge className={`text-xs ${shipment.actualPaymentType === "QR_CODE" ? "bg-purple-100 text-purple-800" : shipment.actualPaymentType === "COD" ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-800"}`}>
                                {shipment.actualPaymentType === "QR_CODE" ? "QR" : shipment.actualPaymentType}
                              </Badge>
                            ) : (
                              <span className="text-sm text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 align-middle text-center">
                            {mapLink ? (
                              <a
                                href={mapLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center text-blue-600 hover:text-blue-700"
                              >
                                <MapPin className="w-4 h-4" />
                              </a>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {tripShipments.length === 0 && (
                <div className="text-center py-12">
                  <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No shipments in this trip</p>
                  {!["COMPLETED", "RECON_FINISHED"].includes(trip.status) && (
                    <Button variant="outline" size="sm" onClick={syncTripTasks} className="mt-3">
                      <RefreshCw className="w-4 h-4 mr-1" /> Sync Tasks
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </PharmeasyLayout>
  );
};

export default PharmeasyTripDetail;
